import { Button } from '@jouzen/ecom-components/v3/Button';
import { Price } from '@jouzen/ecom-components/v3/Price';
import { Typography } from '@jouzen/ecom-components/v3/Typography';
import { useEffect, useState } from 'react';

import { EventType } from '@/analytics/types';
import Image from '@/app/components/Image';
import RichText from '@/app/components/RichText';
import MembershipSelectionEnum from '@/app/enums/MembershipSelectionEnum';
import useCurrency from '@/app/hooks/useCurrency';
import type { ListProduct } from '@/queries/Products';

import type { BuildHardwareOrderBaseProps } from '../_types/order';
import { getPriceAndDiscount } from '../_utils/getPriceAndDiscount';
import HsaFsaEligible from '../BuildHardwareOrder/HsaFsaEligible';

interface CtaContentProps {
  readonly onAddToCart: () => Promise<void>;
  readonly onRadioChange: (value: string) => void;
  readonly order: BuildHardwareOrderBaseProps['order'];
  readonly titleProps: { id?: string | undefined };
  readonly yearlyMembership: ListProduct | undefined;
}

const CtaContent = ({
  onAddToCart,
  onRadioChange,
  order,
  titleProps,
  yearlyMembership,
}: CtaContentProps) => {
  const [pendingAction, setPendingAction] = useState<'skip' | 'add' | null>(
    null,
  );
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const disabled = pendingAction !== null || isAddingToCart;

  // Watch for membership changes and trigger add to cart when state is updated
  useEffect(() => {
    if (
      pendingAction &&
      !isAddingToCart &&
      order.membership.value &&
      ((pendingAction === 'skip' &&
        order.membership.value ===
          MembershipSelectionEnum.OneMonth.toString()) ||
        (pendingAction === 'add' &&
          order.membership.value ===
            MembershipSelectionEnum.OneYear.toString()))
    ) {
      setIsAddingToCart(true);
      setPendingAction(null);
      void onAddToCart();
    }
  }, [order.membership.value, pendingAction, onAddToCart, isAddingToCart]);

  const handleSkip = () => {
    void window.ouraAnalytics.track(EventType.CTAClicked, {
      cta: 'skip for now',
      location: 'modal',
      path: '/cart',
      action: 'skip prepaid modal',
      step: 2,
    });

    setPendingAction('skip');
    onRadioChange(MembershipSelectionEnum.OneMonth);
  };

  const handleAddToCart = () => {
    void window.ouraAnalytics.track(EventType.CTAClicked, {
      cta: 'add to cart',
      location: 'modal',
      path: '/cart',
      action: 'add prepaid to cart',
      step: 2,
    });

    setPendingAction('add');
    onRadioChange(MembershipSelectionEnum.OneYear);
  };

  const { price } = getPriceAndDiscount(yearlyMembership);
  const { currencyCode } = useCurrency();

  return (
    <div data-cy="add-on-modal-cta-content">
      <Typography
        className="mb-6 pr-8 text-2xl"
        weight={'normal'}
        data-cy="bundle_title"
        id={titleProps.id!}
      >
        <RichText messageKey="ab_membership_modal_title" />
      </Typography>
      <div className="flex flex-row items-start justify-center gap-10 md:justify-start">
        <div className="hidden w-full md:block">
          <div className="relative aspect-[386/281] overflow-hidden rounded-[1.25rem] bg-sandstone-450">
            <Image
              fill
              localizedAlt="food_camera_img_alt"
              src="membership-modal-ab/image.jpg"
            />
          </div>
        </div>
        <div className="flex w-full shrink-0 flex-col justify-start md:w-fit">
          <div>
            <HsaFsaEligible isEligible />
            <Typography
              className="mt-3 mb-1 text-xl font-bold md:font-normal"
              color="dark"
              data-cy="one-year-title"
              variant="body-large"
            >
              <RichText messageKey="ab_membership_modal_heading" />
            </Typography>
            <Price
              className="font-bold text-sandstone-500 md:font-normal"
              currencyCode={currencyCode}
              price={price ?? 69.99}
            />
            <ul className="mt-4 mb-6 list-outside list-disc pl-5 text-gray-400">
              <RichText messageKey="ab_membership_modal_list" />
            </ul>
          </div>
          <div className="flex flex-row flex-wrap justify-center gap-4 md:justify-start">
            <Button
              className="flex-1 border-gray-400/50 font-bold text-nowrap md:flex-none md:font-normal"
              data-cy="skip-button"
              onClick={handleSkip}
              variant="outlined-dark"
              disabled={disabled}
            >
              Skip for Now
            </Button>
            <Button
              className="flex-1 font-bold text-nowrap md:flex-none md:font-normal"
              data-cy="add-to-cart-button"
              onClick={handleAddToCart}
              disabled={disabled}
            >
              Add to Cart
            </Button>
          </div>
        </div>
      </div>
      <div className="mt-10 mb-7 h-px bg-gray-300 md:mt-7" />
      <Typography className="italic" color="gray" variant="eyebrow-small">
        <RichText messageKey="add_ons_one_year_prepaid_copy" />
      </Typography>
    </div>
  );
};

export default CtaContent;
